import { Box, Card, CardContent, Typography } from "@mui/material";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import BarChartIcon from "@mui/icons-material/BarChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

/** Match dashboard Finance Overview — Total Income / Total Expenses */
export const INVOICE_INCOME_COLOR = "#6d28d9";
export const INVOICE_EXPENSE_COLOR = "#dc2626";

const INCOME_TYPE_LABELS = {
  REGISTRATION: "Registration",
  CONSULTATION: "Consultation",
  PATHOLOGYTEST: "Pathology Test",
  MEDICINE: "Medicine",
  DONATION: "Donation",
  MISC: "Misc",
  DISCHARGE: "Discharge",
  OTHER: "Other",
};

const EXPENSE_TYPE_LABELS = {
  HOMEOPATHY: "Homeopathy",
  Homeopathy: "Homeopathy",
  ALLOPATHY: "Allopathy",
  Allopathy: "Allopathy",
  PATHOLOGY: "Pathology",
  Pathology: "Pathology",
  OTHER: "Other",
};

const formatIncomeTypeLabel = (type) =>
  INCOME_TYPE_LABELS[type] || INCOME_TYPE_LABELS[String(type).toUpperCase()] || type;

const formatExpenseTypeLabel = (type) =>
  EXPENSE_TYPE_LABELS[type] || EXPENSE_TYPE_LABELS[String(type).toUpperCase()] || type;

/** ₹ abbreviation matching the main dashboard's KPI cards (Cr/L, not raw digit strings) */
const formatAmount = (value) => {
  const num = Number(value) || 0;
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `${sign}₹${(abs / 100000).toFixed(1)}L`;
  return `${sign}₹${abs.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

/** Real month-over-month change — current calendar month vs the prior one, both already
 * present in the same financial-year fetch. `favorableWhenUp=false` inverts the good/bad
 * color (used for Expenses, where a rise is unfavorable). Returns null when there's nothing
 * to compare against (prior month has no data), rather than showing a fabricated 0%. */
const computeTrend = (current, previous, { favorableWhenUp = true } = {}) => {
  if (!previous) return null;
  const change = ((current - previous) / Math.abs(previous)) * 100;
  const isUp = change >= 0;
  const isGood = favorableWhenUp ? isUp : !isUp;
  return { pct: Math.abs(change), isUp, isGood };
};

function TrendPill({ trend, monthLabel, monthValue, loading }) {
  const caption = `${monthLabel}: ${loading ? "…" : formatAmount(monthValue)}`;
  if (!trend) {
    return (
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Typography sx={{ fontSize: "0.64rem", color: "#94a3b8", fontWeight: 700, whiteSpace: "nowrap" }}>
          {caption}
        </Typography>
      </Box>
    );
  }
  const color = trend.isGood ? "#16a34a" : "#dc2626";
  const bg = trend.isGood ? "#dcfce7" : "#fee2e2";
  const Icon = trend.isUp ? TrendingUpIcon : TrendingDownIcon;
  return (
    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.3,
          px: 1,
          py: 0.3,
          borderRadius: 999,
          bgcolor: bg,
          color,
        }}
      >
        <Icon sx={{ fontSize: 14 }} />
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color, lineHeight: 1.3 }}>
          {trend.isUp ? "+" : "-"}
          {trend.pct.toFixed(0)}%
        </Typography>
      </Box>
      <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8", fontWeight: 600, mt: 0.25, whiteSpace: "nowrap" }}>
        {caption}
      </Typography>
    </Box>
  );
}

/** One dashboard-style KPI tile: icon + title/value (value replaces what used to be a
 * subtitle line, keeping the tile to a single compact header row), a trend pill carrying
 * the current month's own figure, and an optional filled-chip by-invoice-type breakdown. */
function SummaryTile({ title, icon, color, fyValue, monthLabel, monthValue, trend, byType, formatLabel, loading }) {
  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
        border: "1px solid #eef1f6",
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          p: "10px 14px !important",
          "&:last-child": { pb: "10px" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 0.75, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.9, minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: `${color}18`,
                color,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.76rem", fontWeight: 700, color: "#64748b", lineHeight: 1.15 }}>
                {title}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: "1.3rem", lineHeight: 1.15, color: "#0f172a", mt: 0.1 }}>
                {loading ? "…" : formatAmount(fyValue)}
              </Typography>
            </Box>
          </Box>
          <TrendPill trend={trend} monthLabel={monthLabel} monthValue={monthValue} loading={loading} />
        </Box>

        {formatLabel && !loading ? (
          <>
            <Box sx={{ borderTop: "1px solid #eef1f6", mt: 0.9, mb: 0.75 }} />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: "auto" }}>
              {byType && byType.length > 0 ? (
                byType.map((item) => (
                  <Box
                    key={item.type}
                    sx={{
                      px: 0.85,
                      py: 0.35,
                      borderRadius: "8px",
                      bgcolor: `${color}14`,
                    }}
                  >
                    <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, color, whiteSpace: "nowrap", lineHeight: 1.3 }}>
                      {formatLabel(item.type)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, color: "#0f172a", whiteSpace: "nowrap", lineHeight: 1.3 }}>
                      {formatAmount(item.amount)}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8" }}>—</Typography>
              )}
            </Box>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function InvoiceSummaryTiles({ summaries, loading = false }) {
  const monthSummary = summaries?.month;
  const priorMonthSummary = summaries?.priorMonth;
  const fySummary = summaries?.financialYear;
  const monthLabel = monthSummary?.label || "This Month";

  const fyIncome = fySummary?.incomeTotal || 0;
  const fyExpense = fySummary?.expenseTotal || 0;
  const monthIncome = monthSummary?.incomeTotal || 0;
  const monthExpense = monthSummary?.expenseTotal || 0;
  const priorMonthIncome = priorMonthSummary?.incomeTotal || 0;
  const priorMonthExpense = priorMonthSummary?.expenseTotal || 0;

  const netFy = fyIncome - fyExpense;
  const netMonth = monthIncome - monthExpense;
  const netPriorMonth = priorMonthIncome - priorMonthExpense;
  const netColor = netFy >= 0 ? "#16a34a" : "#dc2626";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
        gap: 1,
        mb: 1,
      }}
    >
      <SummaryTile
        title="Total Income"
        icon={<CurrencyRupeeIcon sx={{ fontSize: 19 }} />}
        color={INVOICE_INCOME_COLOR}
        fyValue={fyIncome}
        monthLabel={monthLabel}
        monthValue={monthIncome}
        trend={computeTrend(monthIncome, priorMonthIncome, { favorableWhenUp: true })}
        byType={fySummary?.incomeByType || []}
        formatLabel={formatIncomeTypeLabel}
        loading={loading}
      />
      <SummaryTile
        title="Total Expenses"
        icon={<BarChartIcon sx={{ fontSize: 19 }} />}
        color={INVOICE_EXPENSE_COLOR}
        fyValue={fyExpense}
        monthLabel={monthLabel}
        monthValue={monthExpense}
        trend={computeTrend(monthExpense, priorMonthExpense, { favorableWhenUp: false })}
        byType={fySummary?.expenseByType || []}
        formatLabel={formatExpenseTypeLabel}
        loading={loading}
      />
      <SummaryTile
        title="Net Income"
        icon={netFy >= 0 ? <TrendingUpIcon sx={{ fontSize: 19 }} /> : <TrendingDownIcon sx={{ fontSize: 19 }} />}
        color={netColor}
        fyValue={netFy}
        monthLabel={monthLabel}
        monthValue={netMonth}
        trend={computeTrend(netMonth, netPriorMonth, { favorableWhenUp: true })}
        loading={loading}
      />
    </Box>
  );
}

export default InvoiceSummaryTiles;
